import { useMemo, useState, type ReactElement } from 'react';
import type { TimelineSpan } from '../../api/admin.js';
import {
  aggregateByOrigin,
  aggregateGranularByOrigin,
  SPAN_ORIGIN_LABEL,
  type OriginBucket,
  type SpanOrigin,
} from '../../lib/session-stats.js';
import { nanoToUsdCompact } from '../../lib/format.js';
import { kindColor } from '../../lib/spanKind.js';

interface CostOriginDonutProps {
  spans: ReadonlyArray<TimelineSpan>;
  size?: number;
}

const ORIGIN_COLORS: Record<SpanOrigin, string> = {
  turns: 'oklch(70% 0.16 220)',
  distill: 'oklch(72% 0.16 300)',
  analyze: 'oklch(70% 0.17 60)',
  other: 'oklch(60% 0.04 250)',
};

export function CostOriginDonut({ spans, size = 200 }: CostOriginDonutProps): ReactElement {
  const [hoverKey, setHoverKey] = useState<SpanOrigin | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const buckets = useMemo(() => aggregateByOrigin(spans), [spans]);

  const total = useMemo(() => {
    let t = 0n;
    for (const b of buckets) t += b.costNano;
    return t;
  }, [buckets]);

  const totalNumber = useMemo(() => bigintToFractionSafeNumber(total), [total]);

  if (totalNumber === 0 || buckets.length === 0) {
    return (
      <div className="sess-chart-card" data-testid="cost-origin-donut-empty">
        <div className="sess-chart-head">
          <span className="sess-chart-title">Costo por origen</span>
        </div>
        <div className="empty" style={{ height: size }}>
          Sin costos imputados en esta sesión.
        </div>
      </div>
    );
  }

  const slices = computeSlices(buckets, totalNumber, size);

  return (
    <>
      <div className="sess-chart-card" data-testid="cost-origin-donut">
        <div className="sess-chart-head">
          <span className="sess-chart-title">Costo por origen</span>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => setModalOpen(true)}
            data-testid="cost-origin-expand"
            title="Ver detalle por job y kind"
          >
            Ampliar
          </button>
        </div>
        <div className="sess-donut-body">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {slices.map((s) => {
              const dimmed = hoverKey !== null && hoverKey !== s.bucket.origin;
              return (
                <path
                  key={s.bucket.origin}
                  d={s.d}
                  fill={ORIGIN_COLORS[s.bucket.origin]}
                  fillOpacity={dimmed ? 0.25 : 0.95}
                  stroke="var(--surface-0)"
                  strokeWidth={1}
                  onMouseEnter={() => setHoverKey(s.bucket.origin)}
                  onMouseLeave={() => setHoverKey(null)}
                  data-testid={`donut-origin-slice-${s.bucket.origin}`}
                >
                  <title>{`${SPAN_ORIGIN_LABEL[s.bucket.origin]}: ${nanoToUsdCompact(
                    s.bucket.costNano.toString(),
                  )} (${(s.fraction * 100).toFixed(1)}%)`}</title>
                </path>
              );
            })}
            <text
              x={size / 2}
              y={size / 2 - 6}
              textAnchor="middle"
              fontSize={11}
              fill="var(--text-tertiary)"
              fontFamily="var(--font-mono)"
            >
              total
            </text>
            <text
              x={size / 2}
              y={size / 2 + 12}
              textAnchor="middle"
              fontSize={14}
              fill="var(--text-primary)"
              fontFamily="var(--font-mono)"
              fontWeight={500}
            >
              {nanoToUsdCompact(total.toString())}
            </text>
          </svg>
          <ul className="sess-chart-legend">
            {buckets.map((b) => {
              const dimmed = hoverKey !== null && hoverKey !== b.origin;
              const fraction = totalNumber > 0 ? Number(b.costNano) / totalNumber : 0;
              return (
                <li
                  key={b.origin}
                  className={`sess-legend-row${dimmed ? ' is-dim' : ''}`}
                  onMouseEnter={() => setHoverKey(b.origin)}
                  onMouseLeave={() => setHoverKey(null)}
                  data-testid={`donut-origin-legend-${b.origin}`}
                >
                  <span
                    className="sess-legend-swatch"
                    style={{ background: ORIGIN_COLORS[b.origin] }}
                  />
                  <span className="sess-legend-key">{SPAN_ORIGIN_LABEL[b.origin]}</span>
                  <span className="sess-legend-val mono">
                    {nanoToUsdCompact(b.costNano.toString())}
                  </span>
                  <span className="sess-legend-pct mono">
                    {(fraction * 100).toFixed(1)}%
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      {modalOpen ? (
        <OriginBreakdownModal spans={spans} onClose={() => setModalOpen(false)} />
      ) : null}
    </>
  );
}

interface SliceInfo {
  bucket: OriginBucket;
  fraction: number;
  d: string;
}

function computeSlices(
  buckets: ReadonlyArray<OriginBucket>,
  totalNumber: number,
  size: number,
): SliceInfo[] {
  const radius = size / 2;
  const stroke = size * 0.18;
  const innerRadius = radius - stroke;
  const cx = radius;
  const cy = radius;
  let accAngle = -Math.PI / 2;
  return buckets
    .filter((b) => b.costNano > 0n)
    .map((bucket) => {
      const value = bigintToFractionSafeNumber(bucket.costNano);
      const fraction = totalNumber === 0 ? 0 : value / totalNumber;
      const sweep = fraction * Math.PI * 2;
      const startAngle = accAngle;
      const endAngle = accAngle + sweep;
      accAngle = endAngle;
      const x1 = cx + radius * Math.cos(startAngle);
      const y1 = cy + radius * Math.sin(startAngle);
      const x2 = cx + radius * Math.cos(endAngle);
      const y2 = cy + radius * Math.sin(endAngle);
      const xi2 = cx + innerRadius * Math.cos(endAngle);
      const yi2 = cy + innerRadius * Math.sin(endAngle);
      const xi1 = cx + innerRadius * Math.cos(startAngle);
      const yi1 = cy + innerRadius * Math.sin(startAngle);
      const largeArc = sweep > Math.PI ? 1 : 0;
      const d = [
        `M ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
        `L ${xi2} ${yi2}`,
        `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${xi1} ${yi1}`,
        'Z',
      ].join(' ');
      return { bucket, fraction, d };
    });
}

function bigintToFractionSafeNumber(value: bigint): number {
  if (value === 0n) return 0;
  if (value < BigInt(Number.MAX_SAFE_INTEGER)) return Number(value);
  return Number(value / 1_000_000n) * 1_000_000;
}

interface OriginBreakdownModalProps {
  spans: ReadonlyArray<TimelineSpan>;
  onClose: () => void;
}

function OriginBreakdownModal({
  spans,
  onClose,
}: OriginBreakdownModalProps): ReactElement {
  const rows = useMemo(() => aggregateGranularByOrigin(spans), [spans]);
  const total = useMemo(() => {
    let t = 0n;
    for (const r of rows) t += r.costNano;
    return t;
  }, [rows]);

  return (
    <div
      className="mm-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="cost-origin-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="mm-dialog cost-origin-dialog">
        <header className="mm-head">
          <h3 className="mm-title">Costo por origen — detalle</h3>
          <p className="mm-desc">
            Cada fila es la raíz lógica (turn o job) que originó esos costos, o
            la llamada suelta cuando corrió fuera de un turno (ej.{' '}
            <code>anthropic.lookup</code>). El breakdown muestra el costo
            agregado por <code>kind</code> de span dentro de ese origen.
          </p>
        </header>
        <div className="mm-body cost-origin-body">
          {rows.length === 0 ? (
            <div className="empty">Sin costos imputados.</div>
          ) : (
            <table className="cost-origin-tbl">
              <thead>
                <tr>
                  <th>Origen</th>
                  <th>Root</th>
                  <th className="r">Spans</th>
                  <th className="r">Costo</th>
                  <th className="r">%</th>
                  <th>Breakdown por kind</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const fraction =
                    total === 0n
                      ? 0
                      : bigintToFractionSafeNumber(row.costNano) /
                        bigintToFractionSafeNumber(total);
                  const kindRows = Array.from(row.byKind.entries()).sort((a, b) =>
                    a[1] > b[1] ? -1 : 1,
                  );
                  return (
                    <tr key={`${row.origin}::${row.rootName}`}>
                      <td>
                        <span
                          className="sess-legend-swatch"
                          style={{
                            background: ORIGIN_COLORS[row.origin],
                            display: 'inline-block',
                            verticalAlign: 'middle',
                            marginRight: 6,
                          }}
                        />
                        {SPAN_ORIGIN_LABEL[row.origin]}
                      </td>
                      <td className="mono">{row.rootName}</td>
                      <td className="r mono">{row.spanCount}</td>
                      <td className="r mono">
                        {nanoToUsdCompact(row.costNano.toString())}
                      </td>
                      <td className="r mono">{(fraction * 100).toFixed(1)}%</td>
                      <td>
                        <div className="cost-origin-kinds">
                          {kindRows.map(([kind, cost]) => (
                            <span key={kind} className="cost-origin-kind">
                              <span
                                className="kdot"
                                style={{ background: kindColor(kind) }}
                                aria-hidden="true"
                              />
                              <span className="mono">{kind}</span>
                              <span className="mono cost-origin-kind-val">
                                {nanoToUsdCompact(cost.toString())}
                              </span>
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <footer className="mm-foot">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            data-testid="cost-origin-modal-close"
          >
            Cerrar
          </button>
        </footer>
      </div>
    </div>
  );
}
